// A10 — Moliya, to'lovlar, Stars. Module-local data layer (does NOT touch
// `lib/adapters.ts` / `i18n/*`). MyGram's economy is INTERNAL (Take wallet, Ball
// points, per-user tokens, bot Stars): the read/report screens compute from the
// REAL economy tables via `/admin/finance/report/:name`; the config-CRUD +
// workflow screens use `/admin/finance/*` (000241). Every endpoint answers
// {rows:[…]}; fetchers fail-soft to empty so a screen never crashes.
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

/** Every finance row carries at least `id`; the rest is endpoint-specific. */
export interface FinanceRow {
  id: number | string
  [key: string]: unknown
}

function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

/** GET a `{rows:[…]}` envelope, fail-soft to []. */
async function safeRows<T>(path: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<{ rows?: T[] }>(path, { signal })
    return data?.rows ?? []
  } catch {
    return []
  }
}

function qs(params: Record<string, string | number | undefined>): string {
  const u = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') u.set(k, String(v))
  }
  const s = u.toString()
  return s ? `?${s}` : ''
}

/* ── Read/report fetchers (real economy) ──────────────────────────────────────
 * One `/admin/finance/report/:name` dispatch backs every A10 read screen. The
 * quick-search box is forwarded as `q` (the server honours it for the txn search;
 * aggregate reports ignore it and the client-side `searchKeys` filter applies). */
export function financeReport<T extends FinanceRow>(
  name: string,
  searchKeys: string[],
  extra?: Record<string, string>,
): (params: FetchParams) => Promise<FetchResult<T>> {
  return (params: FetchParams) => {
    const query = qs({ limit: 1000, q: params.search.trim(), ...extra })
    return safeRows<T>(`/admin/finance/report/${name}${query}`, params.signal).then((rows) =>
      toResult(rows, params, (r) => searchKeys.map((k) => String(r[k] ?? '')).join(' ')),
    )
  }
}

/* ── Generic config CRUD (provider-settings … alerts) ────────────────────────*/
export function financeConfigList<T extends FinanceRow>(
  kind: string,
  searchKeys: string[],
): (params: FetchParams) => Promise<FetchResult<T>> {
  return (params: FetchParams) =>
    safeRows<T>(`/admin/finance/config/${kind}`, params.signal).then((rows) =>
      toResult(rows, params, (r) => searchKeys.map((k) => String(r[k] ?? '')).join(' ')),
    )
}

export function financeConfigCreate(
  kind: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>(`/admin/finance/config/${kind}`, body)
}

export function financeConfigToggle(
  kind: string,
  id: number | string,
  enabled: boolean,
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/finance/config/${kind}/${id}/toggle`, { enabled })
}

export function financeConfigDelete(kind: string, id: number | string): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/finance/config/${kind}/${encodeURIComponent(String(id))}`)
}

/* ── Refund / dispute queue (A10.11–A10.14) ──────────────────────────────────*/
export function fetchRefunds<T extends FinanceRow>(
  kind: string,
  status: string,
  searchKeys: string[],
): (params: FetchParams) => Promise<FetchResult<T>> {
  return (params: FetchParams) =>
    safeRows<T>(`/admin/finance/refunds${qs({ kind, status })}`, params.signal).then((rows) =>
      toResult(rows, params, (r) => searchKeys.map((k) => String(r[k] ?? '')).join(' ')),
    )
}

export function createRefund(body: {
  kind: string
  txn_ref: string
  user_ref: string
  amount: number
  currency: string
  reason: string
}): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>('/admin/finance/refunds', body)
}

export function decideRefund(
  id: number | string,
  action: 'approve' | 'reject',
  note = '',
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/finance/refunds/${id}/decide`, { action, note })
}

export function bulkDecideRefunds(
  ids: (number | string)[],
  action: 'approve' | 'reject',
): Promise<{ ok: boolean; count: number }> {
  return api.post<{ ok: boolean; count: number }>('/admin/finance/refunds/bulk', { ids, action })
}

/* ── Withdrawal queue (A10.19–A10.21) ────────────────────────────────────────*/
export function fetchWithdrawals<T extends FinanceRow>(
  status: string,
  searchKeys: string[],
): (params: FetchParams) => Promise<FetchResult<T>> {
  return (params: FetchParams) =>
    safeRows<T>(`/admin/finance/withdrawals${qs({ status })}`, params.signal).then((rows) =>
      toResult(rows, params, (r) => searchKeys.map((k) => String(r[k] ?? '')).join(' ')),
    )
}

export function createWithdrawal(body: {
  user_ref: string
  amount: number
  currency: string
  method: string
  destination: string
}): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>('/admin/finance/withdrawals', body)
}

export function decideWithdrawal(
  id: number | string,
  action: 'approve' | 'reject' | 'paid',
  note = '',
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/finance/withdrawals/${id}/decide`, { action, note })
}

/* ── Subscriptions (A10.25 / A10.26) ─────────────────────────────────────────*/
export function fetchSubscriptions<T extends FinanceRow>(
  status: string,
  searchKeys: string[],
): (params: FetchParams) => Promise<FetchResult<T>> {
  return (params: FetchParams) =>
    safeRows<T>(`/admin/finance/subscriptions${qs({ status })}`, params.signal).then((rows) =>
      toResult(rows, params, (r) => searchKeys.map((k) => String(r[k] ?? '')).join(' ')),
    )
}

export function createSubscription(body: {
  user_ref: string
  plan: string
  amount: number
  currency: string
  period: string
}): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>('/admin/finance/subscriptions', body)
}

export function cancelSubscription(id: number | string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/finance/subscriptions/${id}/cancel`, {})
}

export function deleteSubscription(id: number | string): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/finance/subscriptions/${encodeURIComponent(String(id))}`)
}

/* ── Manual compensation (A10.15) — moves the REAL Take wallet ────────────────*/
export function fetchManualTxns<T extends FinanceRow>(
  searchKeys: string[],
): (params: FetchParams) => Promise<FetchResult<T>> {
  return (params: FetchParams) =>
    safeRows<T>('/admin/finance/manual-txns', params.signal).then((rows) =>
      toResult(rows, params, (r) => searchKeys.map((k) => String(r[k] ?? '')).join(' ')),
    )
}

export function createManualTxn(body: {
  username: string
  delta: number
  reason: string
}): Promise<{ ok: boolean; balance: number }> {
  return api.post<{ ok: boolean; balance: number }>('/admin/finance/manual-txns', body)
}

/** Module-local RU strings (mirrors seoApi.SEO_STR — i18n keydan keyin ko'chiriladi). */
export const FIN_STR = {
  // shared
  add: 'Добавить',
  create: 'Создать',
  delete: 'Удалить',
  enable: 'Включить',
  disable: 'Выключить',
  enabled: 'Активно',
  created: 'Создан',
  createdOk: 'Запись создана',
  deletedOk: 'Запись удалена',
  updatedOk: 'Обновлено',
  failed: 'Не удалось выполнить',
  amount: 'Сумма',
  currency: 'Валюта',
  status: 'Статус',
  user: 'Пользователь',
  reason: 'Причина',
  note: 'Заметка',
  method: 'Способ',
  volume: 'Оборот',
  txns: 'Транзакций',
  // statuses
  pending: 'Ожидает',
  approved: 'Одобрено',
  rejected: 'Отклонено',
  paid: 'Выплачено',
  active: 'Активна',
  cancelled: 'Отменена',
  // actions
  approve: 'Одобрить',
  reject: 'Отклонить',
  markPaid: 'Отметить выплаченным',
  cancel: 'Отменить',
  bulkApprove: 'Одобрить выбранные',
  bulkReject: 'Отклонить выбранные',
  // A10.03 failed
  failedTitle: 'Неуспешные платежи',
  failedNote: 'Помеченные анти-фродом платежи (fraud_payments)',
  // A10.04 pending
  pendingTitle: 'Ожидающие платежи',
  pendingNote: 'Средства в эскроу маркета (ожидают расчёта)',
  seller: 'Продавец',
  buyer: 'Покупатель',
  price: 'Цена',
  fee: 'Комиссия',
  // A10.05 provider
  byProviderTitle: 'Разбивка по провайдеру',
  provider: 'Провайдер',
  net: 'Нетто',
  // A10.06 currency
  byCurrencyTitle: 'По валютам',
  // A10.07 country
  byCountryTitle: 'По странам',
  country: 'Страна',
  // A10.08 stars
  starsTitle: 'Эмиссия и оборот Stars',
  bot: 'Бот',
  charges: 'Списаний',
  emitted: 'Выпущено',
  refunded: 'Возвращено',
  circulating: 'В обороте',
  // A10.10 chargebacks
  chargebacksTitle: 'Чарджбеки / возвраты',
  source: 'Источник',
  refId: 'ID операции',
  at: 'Дата',
  // A10.11 disputes
  disputesTitle: 'Споры (dispute)',
  // A10.12 refund queue
  refundQueueTitle: 'Очередь возвратов',
  // A10.13 refund approve
  refundApproveTitle: 'Подтверждение возврата',
  // A10.14 bulk refund
  bulkRefundTitle: 'Массовый возврат',
  txnRef: 'ID транзакции',
  kind: 'Тип',
  refund: 'Возврат',
  dispute: 'Спор',
  // A10.15 manual
  manualTitle: 'Ручная транзакция (компенсация)',
  manualNote: 'Кредит/дебет реального Take-кошелька по @username',
  username: 'Имя пользователя (@username)',
  delta: 'Дельта (+/−)',
  balance: 'Новый баланс',
  compensate: 'Провести',
  compensated: 'Кошелёк изменён',
  // A10.16 search
  searchTitle: 'Поиск транзакций',
  searchNote: 'Поиск по @username / типу / заметке',
  // A10.17 reconciliation
  reconTitle: 'Сверка (Take: баланс ↔ журнал)',
  ledgerSum: 'Сумма журнала',
  diff: 'Расхождение',
  // A10.18 provider recon
  providerReconTitle: 'Сверка баллов (Ball: баланс ↔ журнал)',
  // A10.19 withdrawal requests
  withdrawalsTitle: 'Запросы на вывод',
  destination: 'Назначение',
  // A10.20 withdrawal approve
  withdrawalApproveTitle: 'Подтверждение вывода',
  // A10.21 withdrawal history
  withdrawalHistoryTitle: 'История выводов',
  // A10.22 bot revenue
  botRevenueTitle: 'Доход владельцев ботов',
  revenue: 'Доход',
  // A10.23 channel monetization
  channelMonTitle: 'Монетизация каналов',
  sales: 'Продаж',
  gross: 'Валовой',
  // A10.24 affiliate
  affiliateTitle: 'Партнёрские выплаты',
  referrer: 'Реферер',
  referrals: 'Рефералов',
  pointsPaid: 'Начислено баллов',
  // A10.25 subscriptions
  subsTitle: 'Подписки (recurring)',
  plan: 'План',
  period: 'Период',
  weekly: 'Еженедельно',
  monthly: 'Ежемесячно',
  yearly: 'Ежегодно',
  // A10.26 cancelled subs
  subsCancelledTitle: 'Отменённые подписки',
  // A10.27 cohort LTV
  cohortTitle: 'LTV по когортам',
  cohort: 'Когорта',
  users: 'Пользователей',
  earned: 'Начислено',
  spent: 'Потрачено',
  ltv: 'LTV',
  // A10.28 revenue report
  revenueReportTitle: 'Отчёт о доходах',
  day: 'День',
  events: 'Событий',
  // A10.29 commission report
  commissionReportTitle: 'Отчёт по комиссиям',
  scope: 'Область',
  total: 'Итого',
  // A10.30 tax report
  taxReportTitle: 'Налоговые отчёты',
  taxPeriod: 'Период',
  estTax: 'Налог (оценка 12%)',
  // A10.31 accounting export
  accountingTitle: 'Бухгалтерский экспорт',
  // A10.32 provider settings
  providerSettingsTitle: 'Настройки провайдеров',
  mode: 'Режим',
  live: 'Боевой',
  test: 'Тест',
  disabled: 'Отключён',
  feeNote: 'Примечание',
  addProvider: 'Добавить провайдера',
  // A10.33 commission rates
  commissionRatesTitle: 'Ставки комиссий',
  rateBps: 'Ставка (б.п.)',
  minAmount: 'Мин. сумма',
  maxAmount: 'Макс. сумма',
  addRate: 'Добавить ставку',
  // A10.34 payment limits
  limitsTitle: 'Лимиты платежей',
  addLimit: 'Добавить лимит',
  // A10.35 test mode
  testModeTitle: 'Управление тест-режимом',
  area: 'Область',
  addArea: 'Добавить область',
  // A10.36 alerts
  alertsTitle: 'Финансовые алерты',
  alertName: 'Название',
  metric: 'Метрика',
  threshold: 'Порог',
  windowMin: 'Окно (мин)',
  severity: 'Важность',
  addAlert: 'Добавить алерт',
} as const
